import BaseScene from "../scenes/BaseScene";

export enum ButtonStyle {
  NORMAL = "normal",
}

export interface ButtonStyleConfig {
  width: number;
  height: number;
  radius: number;
  colour: number;
  colourHover: number;
  colourDown: number;
  colourLabel?: number;
}

/**
 * simple text button with hover / down styling
 *
 * @export
 * @class LabelButton
 * @extends {Phaser.GameObjects.Container}
 */
export default class LabelButton extends Phaser.GameObjects.Container {
  /**
   * if input events are enabled.
   *
   * @type {boolean}
   * @memberof LabelButton
   */
  public enabled: boolean = true;

  /**
   * contains style properties of the button
   *
   * @private
   * @type {*}
   * @memberof LabelButton
   */
  private _style: ButtonStyleConfig;

  /**
   * internal callback pointer
   *
   * @private
   * @memberof LabelButton
   */
  private _callback: () => void;

  /**
   *the background game object (currently image)
   *
   * @private
   * @type {Phaser.GameObjects.Image}
   * @memberof LabelButton
   */
  private _background: Phaser.GameObjects.Image;

  /**
   * the label game object (Phaser Text)
   *
   * @private
   * @type {Phaser.GameObjects.Text}
   * @memberof LabelButton
   */
  private _label: Phaser.GameObjects.Text;

  /**
   *Creates an instance of LabelButton.
   * @param {BaseScene} scene the scene to generate texture and target for it to be added.
   * @param {number} x postion in world
   * @param {number} y position in world
   * @param {string} label of text on button
   * @param {() => void} callback function to call on click
   * @param {ButtonStyle} [style=ButtonStyle.NORMAL] a stlye that we wish to associate
   * @memberof LabelButton
   */
  constructor(
    scene: BaseScene,
    x: number,
    y: number,
    label: string,
    callback: () => void,
    style: ButtonStyle = ButtonStyle.NORMAL,
    hoverCallback?: () => void
  ) {
    // super to creat the container at the specified co-ordinates.
    super(scene, x, y);

    // !important - as well are using the new keyword (and not using the scene
    // factory methods, `scene.add` ) we need to add it to the scene manually
    scene.add.existing(this);

    // setup basic style
    this._style = {
      width: 180,
      height: 55,
      radius: 5,
      colour: scene.sponge.data.model.colours.primary,
      colourHover: scene.sponge.data.model.colours.hover,
      colourDown: scene.sponge.data.model.colours.primary_darken,
    };

    // if we dont have a texture for this style button lets create it.
    if (!scene.textures.exists("label-button-" + style)) {
      const _backgroundGraphics = scene.make.graphics({ x: 0, y: 0 }, false);
      _backgroundGraphics.fillStyle(0xffffff, 1);
      _backgroundGraphics.fillRoundedRect(
        0,
        0,
        this._style.width,
        this._style.height,
        this._style.radius
      );
      _backgroundGraphics.generateTexture(
        "label-button-" + style,
        this._style.width,
        this._style.height
      );
    }

    // back background and set inital colour;
    this._background = scene.add.image(0, 0, "label-button-" + style);
    this.colour = this._style.colour;

    // make label
    this._label = scene.add.text(
      0,
      0,
      label,
      scene.sponge.data.model.fonts.button
    );
    this._label.setOrigin(0.5).setFontStyle("900");

    // add to container
    this.add([this._background, this._label]);

    // set up events
    this._callback = callback;

    // as we are working with an grpahics object, we need to set it interaction with some geomerty to detect hit area.
    this._background.setInteractive();

    // on hover entry - we are using "fat arrow" functions "()=> {...", this essentially means
    // we dont need to worry about context, as this allways relates to the current class.
    this._background.on("pointerover", () => {
      if (!this.enabled) return;
      this.colour = this._style.colourHover;

      if (hoverCallback) {
        hoverCallback();
      }
    });

    // on hover exit
    this._background.on("pointerout", () => {
      if (!this.enabled) return;
      this.colour = this._style.colour;
    });

    // on down
    this._background.on("pointerdown", () => {
      if (!this.enabled) return;
      this.colour = this._style.colourDown;
      this._callback();
    });

    // on up
    this._background.on("pointerup", () => {
      if (!this.enabled) return;
      this.colour = this._style.colour;
    });
  }

  /**
   * set label test
   *
   * @memberof LabelButton
   */
  set label(newLabel: string) {
    this._label.text = newLabel;
  }

  /**
   * get label text
   *
   * @type {string}
   * @memberof LabelButton
   */
  get label(): string {
    return this._label.text;
  }

  /**
   * sets the backgorund colour of the button
   *
   * @memberof LabelButton
   */
  set colour(color: number) {
    this._background.tint = color;
  }
}
